import * as THREE from "three";
import { FontLoader, TextGeometry } from "three-stdlib";
import saansTypeface from "../data/saans_semibold.typeface.json";
import monoTypeface from "three/examples/fonts/droid/droid_sans_mono_regular.typeface.json";
import type { CatalogEntry } from "./assetRegistry";
import {
  applyBrandObjectMaterial,
  createBrandMaterial,
  DEFAULT_ACCENT_HEX,
} from "./accent";
import type { BrandCanvasPose, BrandMaterialMode, BrandTextFont, SceneObject } from "./types";
import type { SceneKind } from "../data/sceneKinds";

export const TEXT_LABEL_KIND: SceneKind = "text-label";

export const TEXT_LABEL_CATALOG_ENTRY: CatalogEntry = {
  kind: TEXT_LABEL_KIND,
  master: "__runtime_text_label__",
  label: "3D text",
  group: "Brand",
  available: true,
};

const BRAND_OBJECT_KINDS = new Set<SceneKind>(["icon-mark", "wordmark"]);
const BRAND_CANVAS_KINDS = new Set<SceneKind>(["icon-mark", "wordmark", TEXT_LABEL_KIND]);
const TEXT_SIZE = 1;
const DEFAULT_TEXT = "OneQode";
const DEFAULT_TEXT_DEPTH = 0.06;

const fontLoader = new FontLoader();
const fonts = {
  sans: fontLoader.parse(saansTypeface as any),
  mono: fontLoader.parse(monoTypeface as any),
};

export function isStandaloneBrandObjectKind(kind: SceneKind): boolean {
  return BRAND_OBJECT_KINDS.has(kind);
}

export function isTextLabelKind(kind: SceneKind): boolean {
  return kind === TEXT_LABEL_KIND;
}

export function isBrandCanvasKind(kind: SceneKind): boolean {
  return BRAND_CANVAS_KINDS.has(kind);
}

export function defaultBrandObjectPatch(kind: SceneKind): Partial<SceneObject> {
  if (isTextLabelKind(kind)) {
    return {
      brandPose: "upright",
      textContent: DEFAULT_TEXT,
      textFont: "sans",
      textDepth: DEFAULT_TEXT_DEPTH,
      brandColor: "white",
    };
  }
  if (isStandaloneBrandObjectKind(kind)) {
    return { brandPose: "upright", brandThickness: 1, brandColor: "white" };
  }
  return {};
}

export function resolveBrandCanvasMaterialMode(o: {
  brandColor?: BrandMaterialMode;
  accent?: boolean;
}): BrandMaterialMode {
  if (o.brandColor) return o.brandColor;
  return o.accent ? "accent" : "white";
}

export function resolveBrandMarkMaterialMode(o: {
  brandColor?: BrandMaterialMode;
}): BrandMaterialMode {
  return o.brandColor ?? "white";
}

function brandMaterialTag(mode: BrandMaterialMode, accentHex: number): string {
  return mode === "accent" ? `accent:${accentHex.toString(16)}` : mode;
}

export function addVirtualBrandEntries(groups: Map<string, CatalogEntry[]>): Map<string, CatalogEntry[]> {
  const brandEntries = groups.get("Brand") ?? [];
  if (!brandEntries.some((e) => e.kind === TEXT_LABEL_KIND)) {
    groups.set("Brand", [...brandEntries, TEXT_LABEL_CATALOG_ENTRY]);
  }
  return groups;
}

export function textObjectTag(o: {
  textContent?: string;
  textFont?: BrandTextFont;
  textDepth?: number;
  accent?: boolean;
  brandColor?: BrandMaterialMode;
}, accentHex: number): string {
  const materialMode = resolveBrandCanvasMaterialMode(o);
  return [
    o.textContent ?? DEFAULT_TEXT,
    o.textFont ?? "sans",
    (o.textDepth ?? DEFAULT_TEXT_DEPTH).toFixed(3),
    brandMaterialTag(materialMode, accentHex),
  ].join(":");
}

export function createTextLabelObject(
  o: {
    textContent?: string;
    textFont?: BrandTextFont;
    textDepth?: number;
    accent?: boolean;
    brandColor?: BrandMaterialMode;
  },
  accentHex: number = DEFAULT_ACCENT_HEX,
): THREE.Group {
  const group = new THREE.Group();
  group.name = "text-label";
  group.userData.generatedText = true;

  const text = (o.textContent ?? DEFAULT_TEXT).trim() || DEFAULT_TEXT;
  const fontKey = o.textFont ?? "sans";
  const depth = THREE.MathUtils.clamp(o.textDepth ?? DEFAULT_TEXT_DEPTH, 0.02, 0.8);
  const geometry = new TextGeometry(text, {
    font: fonts[fontKey],
    size: TEXT_SIZE,
    height: depth,
    curveSegments: fontKey === "mono" ? 3 : 5,
    bevelEnabled: true,
    bevelSize: 0.01,
    bevelThickness: 0.01,
  });

  // Centre on X, lift so y=0 is the baseline, centre depth on z so the
  // bevel reads symmetrically when laid flat. We do NOT mirror the geometry:
  // an earlier `geometry.scale(-1, 1, 1)` was compensating for the old
  // flat-pose rotation that buried the front face — now that
  // applyBrandCanvasTransform lays the icon front-face UP, the mirror would
  // render text backward.
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (box) {
    const cx = (box.min.x + box.max.x) / 2;
    geometry.translate(-cx, -box.min.y, -depth / 2);
  }
  geometry.computeVertexNormals();

  const material = createBrandMaterial(resolveBrandCanvasMaterialMode(o), accentHex);
  material.side = THREE.DoubleSide;

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  group.add(mesh);
  return group;
}

export function disposeGeneratedObject(obj: THREE.Object3D): void {
  obj.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose?.();
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) mat.dispose?.();
  });
}

export function applyBrandCanvasAppearance(
  object: THREE.Object3D,
  mode: BrandMaterialMode,
  accentHex: number = DEFAULT_ACCENT_HEX,
): void {
  applyBrandObjectMaterial(object, mode, accentHex);
  object.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) mat.side = THREE.DoubleSide;
  });
}

export function applyBrandCanvasTransform(
  object: THREE.Object3D,
  o: {
    x: number;
    z: number;
    rotation: number;
    scale: number;
    brandPose?: BrandCanvasPose;
    brandFlip?: boolean;
    brandThickness?: number;
    kind?: SceneKind;
  },
  baseY: number,
): void {
  object.position.set(o.x, baseY, o.z);
  const thickness = o.kind && isStandaloneBrandObjectKind(o.kind)
    ? THREE.MathUtils.clamp(o.brandThickness ?? 1, 0.05, 5)
    : 1;
  object.scale.set(o.scale, o.scale, o.scale * thickness);
  const flip = o.brandFlip ? Math.PI : 0;
  if ((o.brandPose ?? "upright") === "flat") {
    // Flat-on-floor pose. Rotating −π/2 around X (instead of +π/2) tips the
    // mark BACKWARDS — i.e. its glyph/front face (authored on +Z, the
    // "viewable" side when upright) ends up pointing +Y (UP). With the old
    // +π/2 the front face was buried in the floor and the camera looked at
    // the icon's BACK, which read as a mirrored / inverse pattern.
    // Rotation/flip then spin around the mark's face normal (now +Y) so
    // direction changes keep the icon flush with the floor.
    object.rotation.set(-Math.PI / 2, 0, o.rotation + flip);
  } else {
    object.rotation.set(0, o.rotation + flip, 0);
  }
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  if (Number.isFinite(box.min.y)) {
    object.position.y += baseY - box.min.y;
  }
}
